import React, { useState } from 'react';
import { TextField, Button, Box, Typography, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom'; // Import the useNavigate hook
import { signUp } from '../services/authService';
import { apiErrorMessage, isNetworkError } from '../utilities/apiError';
import { SsoButtons } from '../components/account/SsoButtons';

// Validation for the form fields
const validateEmail = (email: string) => {
  return /\S+@\S+\.\S+/.test(email);
};

const validatePassword = (password: string) => {
  return password.length >= 6;
};

const SignUpForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [apiError, setApiError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState({ email: '', password: '', confirmPassword: '' });
  const navigate = useNavigate(); // Initialize the navigate hook


  // Handle form submission
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setApiError('');
    setSuccessMessage('');
    let valid = true;

    const newErrors = { email: '', password: '', confirmPassword: '' };

    // Validate email
    if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
      valid = false;
    }

    // Validate password
    if (!validatePassword(password)) {
      newErrors.password = 'Password must be at least 6 characters long';
      valid = false;
    }

    // Validate confirm password
    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      valid = false;
    }

    setErrors(newErrors);


    if (!valid) return; // Stop submission if validation fails

    // Call the API if the form is valid
    try {
      await signUp(email, password);

      setSuccessMessage('Registration successful! Welcome!');
      setTimeout(() => {
          navigate('/')
      }, 2000)
    } catch (error) {
      setApiError(isNetworkError(error) ? 'Network error. Please try again later.' : apiErrorMessage(error));
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '400px',
        margin: '48px auto 0',
        padding: 3,
        gap: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '4px solid',
        borderLeftColor: 'primary.main',
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="h4" textAlign="center" gutterBottom>
        Sign Up
      </Typography>

        {/* API Error Alert */}
        {apiError && <Alert severity="error">{apiError}</Alert>}

        {/* Success Message Alert */}
        {successMessage && <Alert severity="success">{successMessage}</Alert>}

      <TextField
        label="Email"
        variant="outlined"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={!!errors.email}
        helperText={errors.email}
        fullWidth
        required
      />

      <TextField
        label="Password"
        type="password"
        variant="outlined"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={!!errors.password}
        helperText={errors.password}
        fullWidth
        required
      />

      <TextField
        label="Confirm Password"
        type="password"
        variant="outlined"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        error={!!errors.confirmPassword}
        helperText={errors.confirmPassword}
        fullWidth
        required
      />

      <Button type="submit" variant="contained" color="primary" fullWidth>
        Sign Up
      </Button>

      <SsoButtons />
    </Box>
  );
};

export default SignUpForm;